import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";
import { Calendar, Tag } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { NewsItem, CoreValue, JoinStep } from "@/lib/index";
import { hoverLift, springPresets } from "@/lib/motion";

interface NewsCardProps {
  news: NewsItem;
}

export function NewsCard({ news }: NewsCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full overflow-hidden border-border bg-card hover:border-primary/30 transition-colors">
        <div className="aspect-video w-full overflow-hidden">
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-full object-cover"
          />
        </div>
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20">
              <Tag className="w-3 h-3 mr-1" />
              {news.category}
            </Badge>
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="w-3.5 h-3.5 mr-1.5" />
              {news.date}
            </div>
          </div>
          <CardTitle className="text-xl font-semibold leading-tight">
            {news.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-base leading-relaxed">
            {news.excerpt}
          </CardDescription>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface CoreValueCardProps {
  value: CoreValue;
}

export function CoreValueCard({ value }: CoreValueCardProps) {
  const IconComponent = value.icon === "shield" ? () => (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ) : value.icon === "users" ? () => (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ) : value.icon === "heart" ? () => (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ) : () => (
    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full bg-primary text-primary-foreground border-0 shadow-lg">
        <CardHeader>
          <div className="w-16 h-16 rounded-xl bg-primary-foreground/10 flex items-center justify-center mb-4">
            <IconComponent />
          </div>
          <CardTitle className="text-2xl font-bold">
            {value.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-primary-foreground/90 text-base leading-relaxed">
            {value.description}
          </CardDescription>
        </CardContent>
      </Card>
    </motion.div>
  );
}

interface StatCardProps {
  number: string;
  label: string;
}

export function StatCard({ number, label }: StatCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { stiffness: 300, damping: 35 });
  const displayValue = useMotionValue("0");

  useEffect(() => {
    if (isInView) {
      const numericPart = parseInt(number.replace(/\D/g, ""), 10);
      const suffix = number.replace(/[0-9]/g, "");
      
      motionValue.set(numericPart);
      
      const unsubscribe = springValue.on("change", (latest) => {
        displayValue.set(Math.round(latest) + suffix);
      });
      
      return unsubscribe;
    }
  }, [isInView, number, motionValue, springValue, displayValue]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
      transition={springPresets.bouncy}
      className="text-center flex flex-col items-center justify-center"
    >
      <motion.div className="text-5xl font-bold text-primary mb-2 font-mono">
        {isInView ? number : "0"}
      </motion.div>
      <div className="text-lg text-muted-foreground font-medium">
        {label}
      </div>
    </motion.div>
  );
}

interface JoinStepCardProps {
  step: JoinStep;
}

export function JoinStepCard({ step }: JoinStepCardProps) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full border-border bg-card hover:border-primary/30 transition-colors">
        <CardHeader>
          <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mb-4">
            {step.step}
          </div>
          <CardTitle className="text-xl font-semibold">
            {step.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-base leading-relaxed">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </motion.div>
  );
}